import { z } from 'zod'
import { hasContent } from '../../../lib/html'
import { optionalText, requiredId, requiredText } from '../../../lib/forms/rules'
import { COLUMN_BYTES } from '../../../lib/columnBytes'

/**
 * DB kolon tanımıyla birebir uzunluk sınırları (059 spec FR-019).
 *
 * <p><b>İÇERİK İÇİN SINIR YOKTUR</b> (2026-08-25, kullanıcı isteği). `CONTENT_TR`/`CONTENT_EN`
 * **CLOB**'dur — 4000 sınırı DB'den değil uygulamadan geliyordu (`ALL_TAB_COLUMNS`'ta CLOB için
 * görünen 4000, LOB locator boyutudur; CLOB 4 GB tutar). Bu yüzden `CONTENT_MAX_LEN` kaldırıldı.
 * Aşağıdaki ikisi ise **gerçek** `VARCHAR2(1000)` kolonlarıdır ve sınırları korunur.
 */
export const SUBJECT_MAX_LEN = 1000
export const MAIL_MAX_LEN = 1000

/**
 * Bildirim Şablonu form şeması (059 US2).
 *
 * TR konu/içerik **zorunludur** — DB `NULL` kabul etse de uygulama bilinçli olarak daha sıkı davranır
 * (spec FR-015): konusu/içeriği olmayan şablon gönderilemez.
 *
 * <p><b>Mail alanlarında e-posta biçim doğrulaması YOKTUR</b> (2026-08-04): Kime/CC/BCC artık parametre
 * paneli hedefidir ve `${MEETING.OWNER_EMAIL}` gibi **yer tutucu** taşıyabilir — yer tutucu bir adres
 * değildir, adrese gönderim anında çözülür. Bir adres listesi ile yer tutucuların karışımını istemcide
 * doğrulamak, kullanıcının meşru girdisini reddetmek anlamına gelirdi. Geriye yalnız **uzunluk sınırı**
 * kalır (DB kolonu 1000). Aynı gevşetme sunucuda da yapıldı
 * (`NotificationTemplateAdminService.normalizedMail`); ayraç normalizasyonu (`;`) orada sürüyor.
 *
 * <p>Türe bağlı gizleme ve temizleme form bileşeninde yapılır (boş değer zaten geçerlidir).
 *
 * <p><b>İçerik HTML olabilir</b> (059-b: e-posta türünde zengin metin editörü). Bu iki sonucu
 * doğurur:
 *
 * <ol>
 *   <li><b>Uzunluk sınırı yoktur</b> — kaydedilen değer etiketler dahil HTML metnidir ve kolon
 *       CLOB olduğu için üst sınır dayatılmaz. Sayaç da gösterilmez (alanlara `maxLength`
 *       verilmez).</li>
 *   <li><b>"Boş" karakter sayısıyla ölçülemez</b> — boşaltılmış bir editör `<p><br></p>` gibi
 *       biçimsel-dolu ama görünür metni olmayan bir değer üretebilir. Bu yüzden TR içerikte ayrıca
 *       {@link hasContent} aranır: değer HTML ise **görünür metin** şartı işler, düz metin ise
 *       yalnız boşluk olmaması yeterlidir (böylece `<TBD>` gibi meşru bir düz metin reddedilmez).</li>
 * </ol>
 */
export const notificationTemplateFormSchema = z.object({
  operationCodeId: requiredId(),
  notificationTypeId: requiredId(),
  subjectTr: requiredText(SUBJECT_MAX_LEN, COLUMN_BYTES.referenceText),
  subjectEn: optionalText(SUBJECT_MAX_LEN, COLUMN_BYTES.referenceText),
  contentTr: requiredText().refine(hasContent, 'validation.required'),
  contentEn: optionalText(),
  mailTo: optionalText(MAIL_MAX_LEN, COLUMN_BYTES.referenceText),
  mailCc: optionalText(MAIL_MAX_LEN, COLUMN_BYTES.referenceText),
  mailBcc: optionalText(MAIL_MAX_LEN, COLUMN_BYTES.referenceText),
})

export type NotificationTemplateFormValues = z.infer<typeof notificationTemplateFormSchema>
